import React from 'react';

const PrivacyPolicy = () => {
    return (
        <main style={{ maxWidth: 800, margin: '0 auto', padding: '2rem' }}>
            <h1>The Crumb Explorer – Privacy Policy</h1>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>

            <section>
                <h2>1. Overview</h2>
                <p>
                    This Privacy Policy explains how The Crumb Explorer (“App”), operated by
                    <strong> Kyle Hinton – The Simulacrum</strong> (“we”, “us”, or “our”), collects,
                    uses, and shares information when you access or use the App.
                </p>
                <p>
                    By using the App, you agree to the collection and use of information in accordance
                    with this Policy. If you do not agree, please do not use the App.
                </p>
            </section>

            <section>
                <h2>2. Information We May Collect</h2>
                <h3>2.1. Information You Provide</h3>
                <p>
                    In general, The Crumb Explorer does not require you to create a traditional
                    account or provide personal details to browse publicly available data. However,
                    you may choose to contact us directly (for support or feedback), in which case
                    we may collect:
                </p>
                <ul>
                    <li>Your name or alias.</li>
                    <li>Email address or other contact details.</li>
                    <li>Any information you include in your message.</li>
                </ul>

                <h3>2.2. Wallet & Blockchain Data</h3>
                <p>
                    When you connect a Solana wallet to the App, we may interact with public
                    blockchain data associated with your wallet address (e.g. balances, NFTs,
                    transactions). This information is inherently public on the Solana blockchain.
                </p>
                <p>
                    We do <strong>not</strong> collect or store your private keys, seed phrases, or
                    other secret credentials.
                </p>

                <h3>2.3. Usage & Log Data</h3>
                <p>
                    When you use the App, we may collect certain technical information automatically,
                    either directly or through hosting providers and basic analytics, such as:
                </p>
                <ul>
                    <li>IP address and approximate location (derived from IP).</li>
                    <li>Browser type, device type, and operating system.</li>
                    <li>Pages or screens viewed and basic interaction events.</li>
                    <li>Date and time of access, and referring URLs.</li>
                </ul>
                <p>
                    This information helps us understand how the App is used, diagnose issues,
                    and improve performance.
                </p>
            </section>

            <section>
                <h2>3. Cookies and Similar Technologies</h2>
                <p>
                    The App may use cookies or local storage to:
                </p>
                <ul>
                    <li>Remember user preferences.</li>
                    <li>Improve the user experience.</li>
                    <li>Support basic analytics or performance monitoring.</li>
                </ul>
                <p>
                    You can control or disable cookies through your browser settings, but doing so
                    may affect how the App functions.
                </p>
            </section>

            <section>
                <h2>4. How We Use Information</h2>
                <p>We may use the information we collect to:</p>
                <ul>
                    <li>Operate, maintain, and improve the App.</li>
                    <li>Display relevant blockchain data for your wallet or input.</li>
                    <li>Respond to your inquiries and provide support.</li>
                    <li>Monitor usage patterns and detect technical issues.</li>
                    <li>Comply with legal obligations where applicable.</li>
                </ul>
            </section>

            <section>
                <h2>5. How We Share Information</h2>
                <p>
                    We do not sell your personal information. We may share information in the
                    following circumstances:
                </p>
                <ul>
                    <li>
                        <strong>Service Providers:</strong> With hosting, analytics, or infrastructure
                        providers who help us operate the App, subject to appropriate safeguards.
                    </li>
                    <li>
                        <strong>Legal Requirements:</strong> If required by law, regulation, or legal
                        process, or to protect our rights, property, or safety, or that of others.
                    </li>
                    <li>
                        <strong>Business Changes:</strong> In connection with a merger, acquisition,
                        or other business transaction, subject to appropriate confidentiality measures.
                    </li>
                </ul>
            </section>

            <section>
                <h2>6. Third-Party Services & Links</h2>
                <p>
                    The App may link to or integrate third-party services (e.g. Solana RPC providers,
                    wallet adapters, NFT metadata hosts, analytics tools, or external websites). We
                    do not control their privacy practices and are not responsible for their policies.
                    Your use of any third-party service is subject to its own terms and privacy policy.
                </p>
            </section>

            <section>
                <h2>7. Data Retention</h2>
                <p>
                    We retain information only for as long as necessary to fulfill the purposes
                    described in this Policy, unless a longer retention period is required or
                    permitted by law. Aggregated or anonymized data may be retained for analytics
                    and service improvement.
                </p>
            </section>

            <section>
                <h2>8. Security</h2>
                <p>
                    We take reasonable measures to protect information under our control from loss,
                    misuse, or unauthorized access. However, no method of transmission over the
                    internet or method of electronic storage is completely secure, and we cannot
                    guarantee absolute security.
                </p>
            </section>

            <section>
                <h2>9. Children&apos;s Privacy</h2>
                <p>
                    The App is not intended for use by children under the age of 13 (or under the
                    minimum age in your jurisdiction). We do not knowingly collect personal
                    information from children. If you believe that a child has provided us with
                    personal information, please contact us so we can take appropriate action.
                </p>
            </section>

            <section>
                <h2>10. International Users</h2>
                <p>
                    The App may be accessed globally. By using the App, you acknowledge that your
                    information may be processed and stored in jurisdictions that may have different
                    data protection laws than your own.
                </p>
            </section>

            <section>
                <h2>11. Your Choices & Rights</h2>
                <p>
                    Depending on your location, you may have certain rights regarding your personal
                    information (such as access, correction, or deletion). To exercise any such
                    rights, please contact us. We may request additional information to verify your
                    identity before responding.
                </p>
            </section>

            <section>
                <h2>12. Changes to This Privacy Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. When we do, we will revise
                    the “Last updated” date above. Your continued use of the App after changes become
                    effective constitutes your acceptance of the updated Policy.
                </p>
            </section>

            <section>
                <h2>13. Contact</h2>
                <p>
                    If you have questions or concerns about this Privacy Policy, contact:
                </p>
                <p>
                    Email: <a href="mailto:kyle-hinton@outlook.com">kyle-hinton@outlook.com</a><br />
                    Website: <a href="https://thesimulacrum.xyz/" target="_blank" rel="noreferrer">
                    https://thesimulacrum.xyz/
                </a>
                </p>
            </section>
        </main>
    );
};

export default PrivacyPolicy;
